import { twJoin } from '@troon/tailwind-preset/merge';
import { Show } from 'solid-js';
import { IconChevronRight } from '@troon/icons/chevron-right';
import { Picture } from '../components/picture';
import { Heading } from '../components/text';
import { LinkButton } from '../components/link';
import type { ComponentProps, JSX, ParentProps } from 'solid-js';

type P = ParentProps<{
	class?: string;
	level?: ComponentProps<typeof Heading>['as'];
	size?: ComponentProps<typeof Heading>['size'];
	title: string;
	banner?: string;
	bannerAlt?: string;
	img?: string;
	imgAlt?: string;
}>;

type WithLinkProps = P & {
	url: string;
	linkText: JSX.Element;
};
type WithoutLinkProps = P & {
	url?: never;
	linkText?: never;
};

export function Card(props: WithoutLinkProps | WithLinkProps) {
	return (
		<div
			class={twJoin(
				'@container/card relative overflow-hidden rounded-lg border border-neutral',
				props.url && 'group/benefit',
				props.class,
			)}
		>
			<Show when={props.banner}>
				{(bg) => (
					<div class="-z-0 -mb-20 hidden w-full overflow-hidden md:block">
						<Picture
							src={bg()}
							alt={props.bannerAlt ?? ''}
							width={400}
							height={150}
							sizes="26rem"
							class="w-full bg-center object-contain transition-transform group-hover/benefit:scale-105"
							loading="lazy"
							crop="center"
							mode="crop"
						/>
					</div>
				)}
			</Show>
			<div class={twJoin('flex flex-col gap-4 p-4 md:p-6', !props.banner && '@lg/card:flex-row')}>
				<Show when={props.img}>
					{(img) => (
						<div class="z-0 aspect-square size-fit grow-0 rounded-lg border border-neutral bg-white p-2">
							<Picture
								src={img()}
								width={100}
								height={100}
								sizes="6rem"
								class="aspect-square w-24 overflow-hidden rounded-md"
								crop="center"
								alt={props.imgAlt ?? ''}
								loading="lazy"
								mode="contain"
							/>
						</div>
					)}
				</Show>
				<div class="flex flex-col items-start gap-4 text-start">
					<Heading as={props.level ?? 'h4'} size={props.size ?? 'h6'}>
						{props.title}
					</Heading>
					{props.children}
					<Show when={props.url}>
						<LinkButton
							href={props.url}
							class="-ms-2 block w-fit gap-2 text-start after:absolute after:inset-0 after:z-0"
						>
							{props.linkText}
							<IconChevronRight />
						</LinkButton>
					</Show>
				</div>
			</div>
		</div>
	);
}
